import { refreshSession } from '@web/core/auth/session-refresh';
import { ApiError, isDemoExpired, isPasswordChangeRequired, type ApiErrorBody } from './api-error';
import { apiRuntime, CSRF_HEADER, CSRF_HEADER_VALUE } from './api-config';

/**
 * The web's HTTP client: the only place that talks to the API.
 *
 * - The session is in httpOnly cookies set by the API: requests go `credentials:
 *   'same-origin'` and the code never sees a token.
 * - Every request carries the CSRF header.
 * - A 401 on a protected call renews the session once (`refreshSession`, single-flight across
 *   tabs) and retries once. A renewal that fails for good (`invalid`) ends the session: the
 *   handler set with `setSessionExpiredHandler` clears the cache and goes to `/login?next=`.
 *   A renewal that could not reach the API (`unavailable`) keeps the session and surfaces
 *   the error.
 * - A 403 `PASSWORD_CHANGE_REQUIRED` (the user still holds a temporary password) calls the
 *   handler set with `setPasswordChangeRequiredHandler` — main.tsx sends the user to
 *   `/change-password` — and still rejects, so the caller's own error handling runs.
 * - A 403 `DEMO_EXPIRED` (the organization's demo period ended) calls the handler set with
 *   `setDemoExpiredHandler` — main.tsx marks the session, and the shell shows the notice —
 *   and still rejects.
 */

/** Endpoints that ARE the session flow: a 401 there is an answer, not an expired session. */
const SESSION_FLOW_ENDPOINTS = [
  '/auth/login',
  '/auth/select-organization',
  '/auth/refresh',
  '/auth/logout',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
];

let onSessionExpired: () => void = () => undefined;
let onPasswordChangeRequired: () => void = () => undefined;
let onDemoExpired: () => void = () => undefined;

/** Called when the session is definitely over (main.tsx: clear the cache, go to login). */
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

/** Called when the API refuses a call until the password is changed (main.tsx). */
export function setPasswordChangeRequiredHandler(handler: () => void): void {
  onPasswordChangeRequired = handler;
}

/** Called when the API refuses a call because the demo period ended (main.tsx). */
export function setDemoExpiredHandler(handler: () => void): void {
  onDemoExpired = handler;
}

function isSessionFlow(path: string): boolean {
  return SESSION_FLOW_ENDPOINTS.some(
    (endpoint) => path === endpoint || path.startsWith(`${endpoint}/`),
  );
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await open(path, options);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text === '' ? undefined : JSON.parse(text)) as T;
}

/**
 * A POST answered as a stream of JSON lines (NDJSON, the AI assistant): each line is handed to
 * `onLine` as it arrives. Same session, CSRF and error handling as `request`: a failure before
 * the stream starts throws the `ApiError`.
 */
export async function streamLines(
  path: string,
  body: unknown,
  onLine: (line: unknown) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await open(path, { method: 'POST', body, ...(signal ? { signal } : {}) });
  const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader();
  if (!reader) return;
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let newline = buffer.indexOf('\n');
    while (newline >= 0) {
      const line = buffer.slice(0, newline).trim();
      buffer = buffer.slice(newline + 1);
      if (line !== '') onLine(JSON.parse(line));
      newline = buffer.indexOf('\n');
    }
  }
  if (buffer.trim() !== '') onLine(JSON.parse(buffer));
}

/** Sends a request and answers the response when it is OK, renewing the session on a 401. */
async function open(path: string, options: RequestOptions): Promise<Response> {
  let response = await send(path, options);

  if (response.status === 401 && !isSessionFlow(path) && apiRuntime().embedded) {
    // The host owns the session: nothing to refresh here.
    onSessionExpired();
    throw new ApiError(401, await readErrorBody(response));
  }
  if (response.status === 401 && !isSessionFlow(path)) {
    const outcome = await refreshSession();
    if (outcome === 'unavailable') {
      throw new ApiError(503, {
        code: 'SESSION_REFRESH_UNAVAILABLE',
        message: 'Could not renew the session',
      });
    }
    if (outcome === 'ok') response = await send(path, options);
    if (outcome === 'invalid' || response.status === 401) {
      onSessionExpired();
      throw new ApiError(401, await readErrorBody(response));
    }
  }

  if (!response.ok) {
    const error = new ApiError(response.status, await readErrorBody(response));
    if (isPasswordChangeRequired(error)) onPasswordChangeRequired();
    if (isDemoExpired(error)) onDemoExpired();
    throw error;
  }
  return response;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, query, signal } = options;
  const headers = new Headers({ Accept: 'application/json', [CSRF_HEADER]: CSRF_HEADER_VALUE });
  // A form (file upload) sets its own multipart content type, with the boundary.
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers.set('Content-Type', 'application/json');
  try {
    return await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials: apiRuntime().credentials,
      signal: signal ?? null,
      body: body === undefined ? null : isForm ? body : JSON.stringify(body),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, { code: 'NETWORK_UNREACHABLE', message: String(cause) });
  }
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return `${apiRuntime().baseUrl}${path}${qs ? `?${qs}` : ''}`;
}

async function readErrorBody(response: Response): Promise<ApiErrorBody> {
  try {
    const parsed: unknown = await response.clone().json();
    return typeof parsed === 'object' && parsed !== null ? (parsed as ApiErrorBody) : {};
  } catch {
    return {};
  }
}

type WithoutBody = Omit<RequestOptions, 'method' | 'body'>;

/** A `multipart/form-data` POST (a file upload), with the same session and CSRF handling. */
export function upload<T>(path: string, form: FormData, options?: WithoutBody): Promise<T> {
  return request<T>(path, { ...options, method: 'POST', body: form });
}

export const api = {
  get: <T>(path: string, options?: WithoutBody) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: WithoutBody) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: WithoutBody) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: WithoutBody) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: WithoutBody) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
